import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Profile.css';

function Profile() {
  const [tru_customer_id,  setTruCustomerId]   = useState('');
  const [etransfer_email,  setEtransferEmail]  = useState('');
  const [payout_threshold, setPayoutThreshold] = useState('');
  const [payment_type_id,  setPaymentTypeId]   = useState('');

  const [paymentTypes, setPaymentTypes] = useState([]);
  const [minThreshold, setMinThreshold] = useState(null);
  const [maxThreshold, setMaxThreshold] = useState(null);

  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [message,  setMessage]  = useState('');
  const [error,    setError]    = useState('');

  useEffect(() => {
    Promise.all([
      axios.get('/api/profile'),
      axios.get('/api/payment-types'),
      axios.get('/api/config'),
    ])
      .then(([profileRes, typesRes, configRes]) => {
        const p = profileRes.data;
        setTruCustomerId(p.TRU_customer_id ?? '');
        setEtransferEmail(p.etransfer_email  || '');
        setPayoutThreshold(p.payout_threshold ?? '');
        setPaymentTypeId(p.payment_type_id   ?? '');

        setPaymentTypes(typesRes.data);

        const cfg = configRes.data;
        setMinThreshold(cfg.customer_payment_min_threshold?.value_num ?? null);
        setMaxThreshold(cfg.customer_payment_max_threshold?.value_num  ?? null); // remind Ivan there's a typo
      })
      .catch(() => setError('Failed to load profile.'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async e => {
    e.preventDefault();
    setMessage('');
    setError('');

    const selectedType = paymentTypes.find(pt => String(pt.payment_type_id) === String(payment_type_id));
    const isEtransfer  = Number(payment_type_id) === 1;
    const isDonation   = selectedType?.description?.toLowerCase() === 'donation';

    const threshold = payout_threshold !== '' ? Number(payout_threshold) : null;

    if (isEtransfer) {
      if (!etransfer_email) return setError('E-Transfer email is required for this payment method.');
      if (threshold === null) return setError('Payout threshold is required for this payment method.');
    }

    if (threshold !== null) {
      if (minThreshold !== null && threshold < minThreshold) {
        return setError(`Payout threshold must be at least $${minThreshold}.`);
      }
      if (maxThreshold !== null && threshold > maxThreshold) {
        return setError(`Payout threshold cannot exceed $${maxThreshold}.`);
      }
    }

    setSaving(true);
    try {
      await axios.put('/api/profile', {
        etransfer_email:  etransfer_email || null,
        payout_threshold: threshold,
        payment_type_id:  payment_type_id !== '' ? Number(payment_type_id) : null,
      });
      setMessage('Profile saved.');
    } catch {
      setError('Failed to save profile.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="profile-page"><p>Loading…</p></div>;

  const selectedType = paymentTypes.find(pt => String(pt.payment_type_id) === String(payment_type_id));
  const isEtransfer  = Number(payment_type_id) === 1;
  const isDonation   = selectedType?.description?.toLowerCase() === 'donation';

  const thresholdRange = minThreshold !== null && maxThreshold !== null
    ? `Select a value between $${minThreshold} and $${maxThreshold}.`
    : minThreshold !== null
      ? `Select a value of at least $${minThreshold}.`
      : maxThreshold !== null
        ? `Select a value of at most $${maxThreshold}.`
        : '';

  return (
    <div className="profile-page">
      <h2>DROP & GO PROFILE</h2>
      <form className="profile-form" onSubmit={handleSave}>

        <div className="profile-field">
          <span className="profile-field-label">Customer ID</span>
          <span className="profile-static-value">{tru_customer_id}</span>
        </div>

        <div className="profile-field">
          <label htmlFor="payment-type">Payment Method</label>
          <select
            id="payment-type"
            value={payment_type_id}
            onChange={e => setPaymentTypeId(e.target.value)}
          >
            <option value="">— Select —</option>
            {paymentTypes.map(pt => (
              <option key={pt.payment_type_id} value={pt.payment_type_id}>
                {pt.description}
              </option>
            ))}
          </select>
        </div>

        <div className={`profile-field${isDonation ? ' profile-field--disabled' : ''}`}>
          <label htmlFor="etransfer-email">
            E-Transfer Email{isEtransfer && <span className="field-required"> *</span>}
          </label>
          <input
            id="etransfer-email"
            type="email"
            value={etransfer_email}
            onChange={e => setEtransferEmail(e.target.value)}
            placeholder="your@email.com"
            required={isEtransfer}
            disabled={isDonation}
          />
        </div>

        <div className={`profile-field${isDonation ? ' profile-field--disabled' : ''}`}>
          <label htmlFor="payout-threshold">
            Payout Threshold ($){isEtransfer && <span className="field-required"> *</span>}
          </label>
          <input
            id="payout-threshold"
            type="number"
            min={minThreshold ?? 0}
            max={maxThreshold ?? undefined}
            step="any"
            value={payout_threshold}
            onChange={e => setPayoutThreshold(e.target.value)}
            placeholder={minThreshold !== null && maxThreshold !== null ? `${minThreshold}–${maxThreshold}` : 'e.g. 20'}
            required={isEtransfer}
            disabled={isDonation}
          />
          <span className="field-hint">
            {thresholdRange ? `${thresholdRange} ` : ''}You will be paid out once your empty return balance reaches this amount.
          </span>
        </div>

        {message && <p className="profile-success">{message}</p>}
        {error   && <p className="profile-error">{error}</p>}

        <button className="actionButton" type="submit" disabled={saving}>
          {saving ? 'Saving…' : 'SAVE CHANGES'}
        </button>
      </form>
    </div>
  );
}

export default Profile;
